import type { FiscalNote } from '@/types'
import { dispatchCustomerReceiptPrint } from '@/lib/printCustomerReceipt'
import { PRA_QR_PRINT_SIZE_MM } from '@/lib/praBranding'

const CREDIT_NOTE_PAPER_MM = 80 // matches .paper width: 76mm + small margins

function escapeHtml(value: unknown): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function safeMoney(n: number): string {
  if (!Number.isFinite(n)) return '0.00'
  return n.toFixed(2)
}

export function printFbrCreditNote(
  note: FiscalNote,
  opts?: { brand?: string; orderNumber?: string },
): {
  printed: boolean
} {
  const brand = (opts?.brand ?? '').trim() || 'Restaurant POS'
  const createdAt = note.created_at ? new Date(note.created_at) : new Date()
  const createdLabel = createdAt.toLocaleString('en-GB')

  const irn = note.fiscal_details?.irn?.trim() || ''
  // Discriminator: a row with no fiscal_details is a non-FBR refund. Same
  // template, just without the IRN/QR/PRAL footer — the operator still wants
  // a thermal-printed receipt with the same layout the kitchen has trained on.
  const isFiscal = !!note.fiscal_details
  const isPra = String(note.fiscal_details?.authority ?? '').toUpperCase().trim() === 'PRA_EIMS'
  const headingLabel = isFiscal ? 'CREDIT NOTE' : 'REFUND RECEIPT'
  const totalLabel = isFiscal ? 'Total Credit' : 'Total Refund'
  // A PRA credit note is not a PRAL POS document — filing it under the federal
  // authority's name on the customer's copy is simply wrong, and this slip is
  // the artifact a PRA officer would be handed.
  const footerLine = isFiscal
    ? isPra
      ? 'INTEGRATED TAX RECEIPT (PRA e-IMS)'
      : 'INTEGRATED TAX RECEIPT (PRAL POS)'
    : 'REFUND RECEIPT — INTERNAL'
  const orderNumber = (opts?.orderNumber ?? '').trim()
  const qrBase64 = note.fiscal_details?.qr_code_png_base64?.trim() || ''
  // PRA credit notes encode the bare credit-note invoice number, so the symbol
  // is a 21x21 version-1 — 54mm of paper for that is absurd, and the operator
  // asked for it smaller. Inline style because `.qr img` hardcodes 54mm and is
  // shared with the FBR credit note, whose denser payload keeps that size.
  const qrStyle = isPra
    ? ` style="width:${PRA_QR_PRINT_SIZE_MM}mm;height:${PRA_QR_PRINT_SIZE_MM}mm"`
    : ''
  const qrImg = qrBase64
    ? `<div class="qr"><img alt="${isPra ? 'Verify PRA invoice' : 'FBR QR'}"${qrStyle} src="data:image/png;base64,${qrBase64}" /></div>`
    : ''

  const itemsHtml = note.items
    .map((it, idx) => {
      const qty = Number(it.quantity) || 0
      const unit = Number(it.unit_price) || 0
      const total = Number(it.total) || 0
      return `
        <tr>
          <td class="idx">${idx + 1}</td>
          <td class="desc">
            <div class="name">${escapeHtml(it.product_name)}</div>
            <div class="sub muted">${escapeHtml(String(qty))} × ${escapeHtml(safeMoney(unit))}</div>
          </td>
          <td class="num neg">-${escapeHtml(safeMoney(total))}</td>
        </tr>
      `
    })
    .join('')

  const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${isFiscal ? 'Credit Note' : 'Refund Receipt'} ${escapeHtml(irn || note.ref_invoice_number || orderNumber || note.id)}</title>
  <style>
    :root { color-scheme: light; }
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; background: #fff; }
    .paper {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      font-size: 12px;
      line-height: 1.25;
      color: #111;
      width: 76mm;
      margin: 0 auto;
      padding: 10px 10px 12px;
    }
    .center { text-align: center; }
    .muted { color: #6b7280; }
    .dash { border-top: 1px dashed #cbd5e1; margin: 10px 0; }
    h1 { font-size: 14px; margin: 0; letter-spacing: 0.08em; }
    table { width: 100%; border-collapse: collapse; }
    td { vertical-align: top; padding: 4px 0; }
    .idx { width: 14px; padding-right: 6px; }
    .desc { padding-right: 8px; }
    .name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 44mm; }
    .sub { font-size: 10px; margin-top: 2px; }
    .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .neg { color: #b91c1c; font-weight: 700; }
    .kv { display: flex; justify-content: space-between; gap: 10px; padding: 2px 0; }
    .kv .k { color: #6b7280; }
    .kv .v { text-align: right; word-break: break-word; }
    .payable { font-size: 14px; font-weight: 800; }
    .qr img { display: block; margin: 10px auto 0; width: 54mm; height: 54mm; image-rendering: pixelated; }
    .footer { font-size: 10px; margin-top: 8px; }
    @media print {
      @page { margin: 0; }
      .paper { width: auto; }
    }
  </style>
</head>
<body>
  <div class="paper">
    <div class="center">
      <h1>${headingLabel}</h1>
      <div class="muted" style="font-size: 10px; margin-top: 2px;">Generated ${escapeHtml(createdLabel)}</div>
    </div>

    <div class="dash"></div>

    ${
      isFiscal
        ? `<div class="kv"><span class="k">Original IRN</span><span class="v">${escapeHtml(note.ref_invoice_number ?? '')}</span></div>
    ${irn ? `<div class="kv"><span class="k">Credit Note IRN</span><span class="v">${escapeHtml(irn)}</span></div>` : ''}`
        : `${orderNumber ? `<div class="kv"><span class="k">Order #</span><span class="v">${escapeHtml(orderNumber)}</span></div>` : ''}`
    }
    ${note.reason ? `<div class="kv"><span class="k">Reason</span><span class="v">${escapeHtml(note.reason)}</span></div>` : ''}

    <div class="dash"></div>

    <div class="center muted" style="font-size: 10px; margin-bottom: 6px;">ITEMS</div>
    <table>
      ${itemsHtml}
    </table>

    <div class="dash"></div>

    <div class="kv"><span class="k">Subtotal</span><span class="num neg">-${escapeHtml(safeMoney(note.subtotal))}</span></div>
    ${note.tax_amount > 0 ? `<div class="kv"><span class="k">Sales Tax</span><span class="num neg">-${escapeHtml(safeMoney(note.tax_amount))}</span></div>` : ''}
    <div class="kv payable"><span>${totalLabel}</span><span class="num neg">-${escapeHtml(safeMoney(note.total_amount))}</span></div>

    ${qrImg}

    <div class="dash"></div>
    <div class="center footer muted">
      ${escapeHtml(footerLine)}<br/>
      ${escapeHtml(brand)}
    </div>
  </div>
  <script>
    window.addEventListener('afterprint', () => window.close());
  </script>
</body>
</html>`

  // Off-screen-iframe print path (shared with the customer receipt). Replaces
  // the old window.open(blob) approach, which some browsers rerouted to a new
  // tab / search bar instead of printing. paperWidthMm drives the exact-page
  // lock so thermal drivers don't pad to their preset roll length.
  return { printed: dispatchCustomerReceiptPrint(html, CREDIT_NOTE_PAPER_MM) }
}

